/**
 * NMS Check Delivery Date Component
 * Integrates with EDD API to fetch and display delivery estimates
 *
 * CUSTOMIZE (data attributes on the component or a wrapper):
 *   data-edd-buffer="1"     -> extra processing days added on top of the TAT
 *   data-edd-max-days="5"   -> cap the API TAT at this many days (0 = no cap).
 *                              Minimum stays the actual TAT (no floor).
 */

class NMSCheckDeliveryDate extends window.NMSBaseClass {
  constructor(element) {
    super();
    this.element = element;
    this.componentId = element.dataset.componentId || `delivery-${Date.now()}`;

    // Store in global registry
    window.neemans.checkDeliveryDate = window.neemans.checkDeliveryDate || {};
    window.neemans.checkDeliveryDate[this.componentId] = this;

    // Configuration
    this.apiUrl = 'https://pos.neemans.com/api/fetch-nearby-inventoryv2';
    this.apiToken = 'b72622d5-5034-4cd3-b143-21c0e9b2d008';
    this.defaultPincode = '500016';

    // Customizable values. Read from this element OR the nearest ancestor that
    // has the attribute, so it works whether it sits on the component or a wrapper.
    const bufferHost = element.closest('[data-edd-buffer]');
    this.eddBuffer = bufferHost ? (parseInt(bufferHost.dataset.eddBuffer, 10) || 0) : 0;

    const maxHost = element.closest('[data-edd-max-days]');
    this.eddMaxDays = maxHost ? (parseInt(maxHost.dataset.eddMaxDays, 10) || 0) : 0;
  }

  init() {
    // Manual refs setup scoped to this component's element
    this.#setupComponentRefs();
    this.#setupComponentEvents();
    this.#bindAdditionalEvents();
    this.#loadStoredPincode();
    this.#restoreDeliveryState();
  }

  /**
   * Setup refs scoped to this component's element
   * (Override base class behavior that searches entire document)
   */
  #setupComponentRefs() {
    const refElements = this.element.querySelectorAll('[ref]');

    for (const element of refElements) {
      const refName = element.getAttribute('ref');

      if (refName.endsWith('[]')) {
        // Array reference (e.g., ref="buttons[]")
        const arrayName = refName.slice(0, -2);
        if (!this.refs[arrayName]) this.refs[arrayName] = [];
        this.refs[arrayName].push(element);
      } else {
        // Single reference (e.g., ref="button")
        this.refs[refName] = element;
      }
    }
  }

  
  #setupComponentEvents() {
    const clickElements = this.element.querySelectorAll('[data-nms-click]');

    for (const element of clickElements) {
      const handlerPath = element.getAttribute('data-nms-click');
      const [methodName, ...args] = handlerPath.split('/').filter(Boolean);

      if (this[methodName] && typeof this[methodName] === 'function') {
        element.addEventListener('click', event => {
          this[methodName].call(this, event, ...args);
        });
      }
    }
  }

  
  #bindAdditionalEvents() {
    if (this.refs.pincodeInput) {
      // Handle input changes (number filtering + hide delivery info)
      this.refs.pincodeInput.addEventListener('input', event => {
        // Allow only numbers in pincode input
        event.target.value = event.target.value.replace(/[^0-9]/g, '');
        // Hide delivery info immediately when user modifies input
        this.#hideDeliveryInfo();
      });

      // Handle Enter key press
      this.refs.pincodeInput.addEventListener('keypress', event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.handlePincodeChange();
        }
      });
    }

    // Listen for delivery updates from other components (like cart)
    document.addEventListener('nms:delivery-date-updated', event => {
      const { pincode, deliveryDate, eddDays, source } = event.detail;
      // Only sync if it's from a different component (avoid infinite loops)
      if (source === 'cart-delivery') {
        this.#syncFromOtherComponent(pincode, deliveryDate, eddDays);
      }
    });
  }

  /**
   * Event handler for pincode check button clicks
   * Auto-bound via data-nms-click="/handlePincodeChange"
   */
  handlePincodeChange(event) {
    if (event) event.preventDefault();

    // If button says "CHANGE", clear input and hide delivery info
    if (this.refs.checkButton && this.refs.checkButton.textContent === 'CHANGE') {
      if (this.refs.pincodeInput) this.refs.pincodeInput.value = '';
      this.#hideDeliveryInfo();
      return;
    }

    const pincode = this.refs.pincodeInput?.value?.trim();

    if (!pincode) {
      this.#showError('Please enter pincode.');
      return;
    }

    if (pincode.length < 5 || pincode.length > 6) {
      this.#showError('Please enter a valid pincode.');
      return;
    }

    // Don't store pincode yet - only store after successful check
    // This prevents error pincodes from being saved
    this.#checkDeliveryDate(pincode);
  }

  #loadStoredPincode() {
    if (!this.refs.pincodeInput) return;

    try {
      const storedPincode = localStorage.getItem('neemans:delivery-pincode');
      if (storedPincode) {
        this.refs.pincodeInput.value = storedPincode;
        // Don't auto-load delivery info on page load
      } else {
        // Don't show default pincode - leave input empty
        // User should enter their own pincode
        this.refs.pincodeInput.value = '';
      }
    } catch (error) {
      // On error, leave input empty instead of showing default
      this.refs.pincodeInput.value = '';
    }
  }

  /**
   * Restore delivery state from sessionStorage
   * Shows the previously checked delivery information on page refresh
   * Only restores successful delivery checks, not errors
   */
  #restoreDeliveryState() {
    try {
      const savedState = sessionStorage.getItem('neemans:delivery-state');

      if (!savedState) return;

      const state = JSON.parse(savedState);

      // Verify the state is valid and successful (not an error)
      if (!state.pincode || !state.timestamp || state.isError) return;

      // Only restore if we have valid delivery data
      if (!state.eddDays || !state.formattedDate) return;

      // Restore pincode to input
      if (this.refs.pincodeInput && state.pincode) {
        this.refs.pincodeInput.value = state.pincode;
      }

      // Recompute from the raw stored days so the CURRENT cap/buffer applies
      // (otherwise a cached date would ignore changed Customize settings).
      let formattedDate = state.formattedDate;
      if (typeof state.eddDays === 'number') {
        formattedDate = this.#formatDeliveryDate(this.#calculateDeliveryDate(state.eddDays));
      }

      // Restore delivery information
      if (this.refs.deliveryDate) {
        this.refs.deliveryDate.textContent = `Delivery by ${formattedDate}`;
      }

      // Show success info, hide error
      if (this.refs.deliverySuccess) this.refs.deliverySuccess.style.display = 'flex';
      if (this.refs.deliveryError) this.refs.deliveryError.style.display = 'none';

      // Show delivery info container
      if (this.refs.deliveryInfo) {
        this.refs.deliveryInfo.classList.remove('nms-check-delivery-date__delivery-info--hidden');
      }

      // Change button text to "CHANGE"
      if (this.refs.checkButton) {
        this.refs.checkButton.textContent = 'CHANGE';
      }
    } catch (error) {
      // Silently fail if sessionStorage is not available or data is corrupted
      console.log('Failed to restore delivery state:', error);
    }
  }

  /**
   * Save delivery state to sessionStorage
   * Only saves successful delivery check results across page refreshes in the same tab
   * Errors are NOT persisted
   */
  #saveDeliveryState(pincode, options = {}) {
    try {
      const state = {
        pincode,
        timestamp: Date.now(),
        isError: false, // Always false - we only save successful checks
        ...options,
      };

      sessionStorage.setItem('neemans:delivery-state', JSON.stringify(state));
    } catch (error) {
      // Silently fail if sessionStorage is not available
      console.log('Failed to save delivery state:', error);
    }
  }

  async #checkDeliveryDate(pincode) {
    this.showLoading(this.refs.checkButton);

    try {
      const sku = await this.#resolveSku();

      if (!sku) {
        this.#showError('Product information not available');
        return;
      }

      const eddData = await this.#fetchEDD(pincode, sku);

      // Handle API response - use minDays if available, otherwise use maxDays
      //const minDays = eddData.maxDays || eddData.minDays || 4;
      const minDays = eddData.minDays || eddData.maxDays || 4;
      const finalEDD = this.#calculateFinalEDD([minDays]);

      // Store successful pincode in localStorage
      try {
        localStorage.setItem('neemans:delivery-pincode', pincode);
      } catch (error) {
        // Silently fail if localStorage is not available
      }

      this.#displayDeliveryInfo(finalEDD);
      this.#broadcastDeliveryUpdate(pincode, finalEDD);
    } catch (error) {
      console.log('API Error:', error.message); // Debug log

      // Check if pincode is not serviceable - check for various error patterns
      const isNotServiceable =
        error.message?.includes('not serviceable') ||
        error.message?.includes('400') ||
        error.message?.includes('HTTP 400') ||
        error.message?.toLowerCase().includes('not available') ||
        error.message?.toLowerCase().includes('invalid pincode') ||
        error.message?.includes('The provided pincode is not valid') ||
        error.message?.includes('not valid');

      // Clear sessionStorage so refresh doesn't restore old successful state
      try {
        sessionStorage.removeItem('neemans:delivery-state');
        // Also clear localStorage to prevent showing old pincode on refresh
        localStorage.removeItem('neemans:delivery-pincode');
        localStorage.removeItem('neemans:delivery-data');
      } catch (error) {
        // Silently fail if storage is not available
      }

      // Clear the input field
      if (this.refs.pincodeInput) {
        this.refs.pincodeInput.value = '';
      }

      // Broadcast clear event to cart to remove delivery data
      document.dispatchEvent(
        new CustomEvent('nms:delivery-date-cleared', {
          detail: {
            source: 'product-delivery-error',
          },
        })
      );

      if (isNotServiceable) {
        this.#showError('This Pincode is not serviceable. Please try another Pincode.');
      } else {
        // Show generic error for other failures (network issues, etc.)
        // Don't broadcast or store failed pincodes
        this.#showError('Unable to check delivery. Please try again.');
      }
    } finally {
      this.hideLoading(this.refs.checkButton);
    }
  }

  async #fetchEDD(pincode, sku, quantity = 1) {
    const requestBody = {
      customerPincode: parseInt(pincode),
      skuCode: sku,
      quantity: quantity,
      context_tag: 'product_page',
    };

    const response = await fetch(this.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.apiToken}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      // Try to get error details from response
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch (e) {
        // Could not parse error response
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();

    if (data.status !== 'success') {
      throw new Error(data.message || 'API request failed');
    }

    return data;
  }

  /**
   * Resolve the SKU of the CURRENTLY selected variant.
   *  1) Read the selected variant id from the product form (updates on size change),
   *     then look up that variant's SKU in the product JSON (/products/handle.js).
   *     This is reliable for pills, radios, or dropdowns.
   *  2) Fallback to the synchronous DOM detection (#getCurrentProductSku).
   */
  async #resolveSku() {
    try {
      const form = document.querySelector('form[action*="/cart/add"]');
      const variantId =
        form?.querySelector('input[name="id"]')?.value ||
        document.querySelector('variant-selects input[name="id"]:checked')?.value ||
        document.querySelector('[name="id"]')?.value ||
        null;

      if (variantId) {
        const path = window.location.pathname.split('?')[0].replace(/\/$/, '');
        const res = await fetch(`${path}.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (res.ok) {
          const product = await res.json();
          const variant = (product.variants || []).find(
            v => String(v.id) === String(variantId)
          );
          if (variant && variant.sku) return variant.sku;
        }
      }
    } catch (error) {
      // Fall through to DOM detection
    }

    // Fallback: existing synchronous detection
    return this.#getCurrentProductSku();
  }

  #getCurrentProductSku() {
    // METHOD 1: Try to get SKU from variant selector dropdown
    const variantSelect = document.querySelector('variant-selects select[name="id"]');
    if (variantSelect) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      const sku = selectedOption?.dataset?.sku;
      if (sku) return sku;
    }

    // METHOD 2: Try to get from Shopify's global product object
    if (window.ShopifyAnalytics?.meta?.product) {
      const product = window.ShopifyAnalytics.meta.product;
      if (product.variants && product.variants.length > 0) {
        const currentVariant = product.variants.find(v => v.id === product.variant_id) || product.variants[0];
        if (currentVariant?.sku) return currentVariant.sku;
      }
    }

    // METHOD 3: Get from product form + JSON scripts (CURRENTLY WORKING METHOD)
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput?.value) {
        // Extract SKU from Shopify's JSON data scripts
        const scripts = document.querySelectorAll('script[type="application/json"]');
        for (let i = 0; i < scripts.length; i++) {
          try {
            const data = JSON.parse(scripts[i].textContent);

            // Script contains current variant data with direct id/sku mapping
            if (data.id && data.sku && data.id == variantInput.value) {
              return data.sku;
            }

            if (data.variants) {
              const variant = data.variants.find(v => v.id == variantInput.value);
              if (variant?.sku) return variant.sku;
            }

            if (data.product?.variants) {
              const variant = data.product.variants.find(v => v.id == variantInput.value);
              if (variant?.sku) return variant.sku;
            }
          } catch (e) {
            // Continue to next script
          }
        }
      }
    }

    // Final fallback: try to extract from page content
    const skuElement = document.querySelector('.variant-sku');
    if (skuElement) {
      const skuMatch = skuElement.textContent.match(/NM-[A-Z0-9-]+/);
      if (skuMatch) return skuMatch[0];
    }

    // Try alternative selectors
    const skuSpan = document.querySelector('[data-testid="product-sku"], .product-sku, .sku');
    if (skuSpan) {
      const skuMatch = skuSpan.textContent.match(/NM-[A-Z0-9-]+/);
      if (skuMatch) return skuMatch[0];
    }

    return null;
  }

  /**
   * Return the raw TAT (slowest). The configurable cap (eddMaxDays) and buffer
   * (eddBuffer) are applied in #calculateDeliveryDate so they also affect
   * cached/restored dates.
   */
  #calculateFinalEDD(minDaysArray) {
    return Math.max(...minDaysArray);
  }

  #displayDeliveryInfo(eddDays) {
    if (!this.refs.deliveryInfo || !this.refs.deliveryDate) return;

    const deliveryDate = this.#calculateDeliveryDate(eddDays);
    const formattedDate = this.#formatDeliveryDate(deliveryDate);

    // Update delivery date text
    this.refs.deliveryDate.textContent = `Delivery by ${formattedDate}`;

    // Show success info, hide error
    if (this.refs.deliverySuccess) this.refs.deliverySuccess.style.display = 'flex';
    if (this.refs.deliveryError) this.refs.deliveryError.style.display = 'none';

    // Show delivery info container
    this.refs.deliveryInfo.classList.remove('nms-check-delivery-date__delivery-info--hidden');

    // Change button text to "CHANGE" when delivery info is shown
    if (this.refs.checkButton) {
      this.refs.checkButton.textContent = 'CHANGE';
    }

    // Save delivery state to sessionStorage (store RAW eddDays)
    const pincode = this.refs.pincodeInput?.value?.trim();
    if (pincode) {
      this.#saveDeliveryState(pincode, {
        eddDays,
        deliveryDate: deliveryDate.toISOString(),
        formattedDate,
        isError: false,
      });
    }
  }

  /**
   * Compute the delivery date from raw TAT days.
   * Applies the Customize cap (eddMaxDays) then the buffer (eddBuffer).
   */
  #calculateDeliveryDate(days) {
    let effectiveDays = days || 0;

    // Max cap: if set (>0), never promise more than this many API days.
    // Minimum stays the actual TAT (no floor).
    if (this.eddMaxDays > 0) {
      effectiveDays = Math.min(effectiveDays, this.eddMaxDays);
    }

    // Buffer: extra processing days added on top.
    effectiveDays = effectiveDays + this.eddBuffer;

    const today = new Date();
    const deliveryDate = new Date(today);
    deliveryDate.setDate(today.getDate() + effectiveDays);
    return deliveryDate;
  }

  #formatDeliveryDate(date) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const ordinal = this.#getOrdinal(day);

    return `${dayName}, ${day}${ordinal} ${month}.`;
  }

  #getOrdinal(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  #showError(message) {
    if (!this.refs.deliveryInfo || !this.refs.deliveryError || !this.refs.deliveryErrorText) return;

    // Update error message
    this.refs.deliveryErrorText.textContent = message;

    // Show error, hide success info
    if (this.refs.deliveryError) this.refs.deliveryError.style.display = 'flex';
    if (this.refs.deliverySuccess) this.refs.deliverySuccess.style.display = 'none';

    // Show delivery info container
    this.refs.deliveryInfo.classList.remove('nms-check-delivery-date__delivery-info--hidden');

    // Change button text to "CHANGE" when error is shown
    if (this.refs.checkButton) {
      this.refs.checkButton.textContent = 'CHANGE';
    }

    // Don't save error states to sessionStorage
    // Only successful delivery checks are persisted
  }

  #hideDeliveryInfo() {
    if (!this.refs.deliveryInfo) return;

    // Hide delivery info container
    this.refs.deliveryInfo.classList.add('nms-check-delivery-date__delivery-info--hidden');

    // Reset button text to "CHECK" when delivery info is hidden
    if (this.refs.checkButton) {
      this.refs.checkButton.textContent = 'CHECK';
    }

    // Clear delivery state from sessionStorage when user hides the info
    try {
      sessionStorage.removeItem('neemans:delivery-state');
    } catch (error) {
      // Silently fail if sessionStorage is not available
    }
  }

  // Override inherited showLoading to add custom loader for delivery component
  showLoading(element) {
    if (!element) return;

    element.classList.add('nms-loading');
    element.setAttribute('aria-busy', 'true');

    const originalText = element.textContent;
    element.dataset.originalText = originalText;
    element.disabled = true;

    // Create CSS loader HTML
    const loaderHTML = `<div class="nms-delivery-date-loader"></div>`;

    element.innerHTML = loaderHTML;
  }

  // Override inherited hideLoading for delivery-specific behavior
  hideLoading(element) {
    if (!element) return;

    element.classList.remove('nms-loading');
    element.setAttribute('aria-busy', 'false');
    element.disabled = false;

    // Don't restore original text for the check button - let displayDeliveryInfo/showError handle it
    if (element === this.refs.checkButton) {
      // Button text will be set by displayDeliveryInfo() or showError()
      // Just reset innerHTML to avoid leftover loader content
      element.innerHTML = element.dataset.originalText || 'CHECK';
      delete element.dataset.originalText;
    } else {
      element.textContent = element.dataset.originalText || element.textContent;
      delete element.dataset.originalText;
    }
  }

  /**
   * Sync delivery data from other components (like cart)
   */
  #syncFromOtherComponent(pincode, deliveryDate, eddDays) {
    // Update input field with the new pincode
    if (this.refs.pincodeInput) {
      this.refs.pincodeInput.value = pincode;
    }

    // Calculate and display the delivery info
    const deliveryDateObj = this.#calculateDeliveryDate(eddDays);
    const formattedDate = this.#formatDeliveryDate(deliveryDateObj);

    // Display the delivery info (this will also save to sessionStorage)
    this.#displayDeliveryInfo(eddDays);

    // Store the data in localStorage as well for backwards compatibility
    try {
      localStorage.setItem('neemans:delivery-pincode', pincode);
      const deliveryData = {
        eddDays,
        deliveryDate: deliveryDateObj.toISOString(),
        formattedDate,
        timestamp: Date.now(),
      };
      localStorage.setItem('neemans:delivery-data', JSON.stringify(deliveryData));
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }

  /**
   * Broadcast delivery update to other components (like cart delivery)
   */
  #broadcastDeliveryUpdate(pincode, eddDays) {
    const deliveryDate = this.#calculateDeliveryDate(eddDays);
    const formattedDate = this.#formatDeliveryDate(deliveryDate);

    document.dispatchEvent(
      new CustomEvent('nms:delivery-date-updated', {
        detail: {
          pincode,
          deliveryDate: deliveryDate.toISOString(),
          formattedDate,
          eddDays,
          source: 'product-delivery',
        },
      })
    );
  }

  destroy() {
    super.destroy();
    // Clean up global reference
    if (window.neemans.checkDeliveryDate?.[this.componentId]) {
      delete window.neemans.checkDeliveryDate[this.componentId];
    }
  }
}

/**
 * Auto-initialize all delivery date components
 */
function initNMSCheckDeliveryDate(container = document) {
  if (!window.NMSBaseClass) {
    console.error('NMSBaseClass required for delivery date components');
    return;
  }

  const elements = container.querySelectorAll('.nms-check-delivery-date:not([data-initialized])');

  for (const element of elements) {
    const instance = new NMSCheckDeliveryDate(element);
    instance.init();
    element.setAttribute('data-initialized', 'true');
  }
}

// Initialize when ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSCheckDeliveryDate);
} else {
  initNMSCheckDeliveryDate();
}

// Listen for color content updates and re-initialize within the updated container
document.addEventListener('nms:color-content-updated', event => {
  const { container, source } = event.detail;
  initNMSCheckDeliveryDate(container);
});

// Re-initialize on section changes (theme editor, AJAX)
document.addEventListener('shopify:section:load', initNMSCheckDeliveryDate);

// Make available globally for checkout integration
window.neemans.NMSCheckDeliveryDate = NMSCheckDeliveryDate;
window.NMSCheckDeliveryDate = NMSCheckDeliveryDate;